<script setup lang="ts">
import { type Component, computed } from "vue";

import PageIcon from "~/components/dumb/PageIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { ExpandIcon, XIcon } from "~/icons";
import { IconSize } from "~/shared/enums";
import type { Page } from "~/shared/types";

const props = defineProps<{
  title: string;
  page?: Page;
  icon?: Component;
  iconArgs?: object;
  strikethrough?: boolean;
  showExpandIcon?: boolean;
  isNotificationMode?: boolean;
  isChatEnabledMode?: boolean;
  isChatDisabledMode?: boolean;
}>();

const emit = defineEmits<{
  expand: [];
  remove: [];
  click: [event: MouseEvent];
  keydown: [event: KeyboardEvent];
  contextMenu: [MouseEvent];
}>();

const titleSplit = computed(() => props.title.split("\n"));
</script>

<template>
  <button
    type="button"
    class="group/page-link relative inline-flex min-h-6 min-w-0 max-w-64 shrink cursor-pointer items-center gap-1.5 rounded px-1 decoration-gray-700 text-md dark:decoration-zinc-300"
    :class="{
      '!min-h-5': isChatEnabledMode || isChatDisabledMode,
      'bg-lt': !isNotificationMode && !isChatDisabledMode,
      'bg-md': isChatDisabledMode,
      'hover:bg-opposite/10': isNotificationMode,
      'hover:bg-md': !isNotificationMode && !isChatEnabledMode && !isChatDisabledMode,
    }"
    @contextmenu="emit('contextMenu', $event)"
    @click="emit('click', $event)"
    @keydown="emit('keydown', $event)">
    <div class="flex min-w-0 items-center gap-1.5 overflow-hidden">
      <PageIcon v-if="page" :page="page" :size="IconSize.S" />
      <component :is="icon" v-else-if="icon" v-bind="iconArgs" class="icon-sm" />
      <span class="truncate text-left" :class="strikethrough && 'line-through'">
        <template v-if="isNotificationMode">
          <template v-for="(line, index) in titleSplit" :key="index">
            {{ line }}
            <br v-if="index < titleSplit.length - 1" />
          </template>
        </template>
        <template v-else>
          {{ title }}
        </template>
      </span>
    </div>
    <Tooltip v-if="showExpandIcon" text="Show">
      <span class="rounded p-0.5 hover:bg-hvy" @click.stop="emit('expand')" @keydown.stop.enter="emit('expand')">
        <ExpandIcon class="icon-sm" />
      </span>
    </Tooltip>
    <template v-if="isChatEnabledMode">
      <!-- dummy elements to help avoid overshooting the x -->
      <div class="absolute -top-1.5 right-0 hidden h-1.5 w-3 group-hover/page-link:flex" />
      <div class="absolute -right-1.5 top-0 hidden h-3 w-1.5 group-hover/page-link:flex" />
      <button
        type="button"
        class="absolute -right-1.5 -top-1.5 hidden cursor-pointer items-center justify-center rounded-full border bg-lt border-md icon-xs group-hover/page-link:flex"
        aria-label="Remove context"
        @click.stop="emit('remove')"
        @keydown.enter="emit('remove')">
        <Tooltip text="Remove context">
          <XIcon class="size-full text-lt focus:outline-none" />
        </Tooltip>
      </button>
    </template>
  </button>
</template>
